"use client";

import { useCallback, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { fetchWithdrawalRequest } from "@jti/rest-api";
import type { WithdrawalFormValues } from "@jti/ui/components/WithdrawalFormModal";
import { useUser } from "@jti/ui/hooks/useUser";
import { formatDate } from "@jti/ui/utils/formatDate";

import { useLocale } from "@/context/LocaleContext";

import { useWithdrawalFormMessage } from "./useWithdrawalFormMessage";

export const useFooterWithdrawal = () => {
  const {
    isWithdrawalFormForLoggedInEnabled,
    isWithdrawalFormForNotLoggedInEnabled,
    withdrawalOrderConfig,
    isWithdrawalFeatureEnabled,
  } = useConfig();
  const { user } = useUser();
  const { currentLocale, defaultLocale } = useLocale();
  const isLoggedIn = !!user?.ciam_token;
  const isWithdrawalEnabledForCurrentUser = Boolean(
    (isLoggedIn && isWithdrawalFormForLoggedInEnabled && isWithdrawalFeatureEnabled) ||
    (!isLoggedIn && isWithdrawalFormForNotLoggedInEnabled && isWithdrawalFeatureEnabled)
  );

  const withdrawalMessagePayload = {
    product: withdrawalOrderConfig?.withdrawalMessagePayload?.product,
    language: currentLocale || defaultLocale,
    market: withdrawalOrderConfig?.withdrawalMessagePayload?.market,
  };

  const [withdrawalStep, setWithdrawalStep] = useState<null | "confirm" | "success">(null);
  const [withdrawalError, setWithdrawalError] = useState<string | null>(null);
  const [withdrawalSubmittedOn, setWithdrawalSubmittedOn] = useState("");
  const [isWithdrawalAuthenticatedLoading, setIsWithdrawalAuthenticatedLoading] = useState(false);
  const { mutateAsync: sendWithdrawalFormMessage, isLoading: isWithdrawalFormLoading } = useWithdrawalFormMessage();

  const openWithdrawalModal = useCallback(() => {
    setWithdrawalSubmittedOn("");
    setWithdrawalStep("confirm");
  }, []);

  const closeWithdrawalModal = useCallback(() => {
    setWithdrawalStep(null);
    setWithdrawalError(null);
    setWithdrawalSubmittedOn("");
  }, []);

  const handleWithdrawalConfirmUnauthenticated = async (values: WithdrawalFormValues) => {
    setWithdrawalError(null);
    try {
      await sendWithdrawalFormMessage(values, withdrawalMessagePayload);
      setWithdrawalSubmittedOn(
        // I don't know if we should do this, please review
        new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZoneName: "short",
        }).format(new Date())
      );
      setWithdrawalStep("success");
    } catch {
      setWithdrawalError(withdrawalOrderConfig?.withdrawalRequestError ?? "");
    }
  };

  const handleWithdrawalConfirmAuthenticated = async (values: WithdrawalFormValues) => {
    setWithdrawalError(null);
    setIsWithdrawalAuthenticatedLoading(true);
    try {
      const response = await fetchWithdrawalRequest({
        orderId: values.selectedOrderValue,
        firstName: values.firstName || undefined,
        lastName: values.lastName || undefined,
        email: values.email || undefined,
        phone: values.phoneNumber || undefined,
      });
      if (response?.data.created_at) {
        setWithdrawalSubmittedOn(formatDate(response.data.created_at, "DD MMM YYYY, HH:mm"));
      }
      setWithdrawalStep("success");
    } catch {
      setWithdrawalError(withdrawalOrderConfig?.withdrawalRequestError ?? "");
    } finally {
      setIsWithdrawalAuthenticatedLoading(false);
    }
  };

  return {
    isWithdrawalEnabledForCurrentUser,
    isLoggedIn,
    withdrawalStep,
    withdrawalError,
    withdrawalSubmittedOn,
    isWithdrawalFormLoading: isWithdrawalFormLoading || isWithdrawalAuthenticatedLoading,
    openWithdrawalModal,
    closeWithdrawalModal,
    handleWithdrawalConfirmUnauthenticated,
    handleWithdrawalConfirmAuthenticated,
  };
};
